import "./attendanceStyle.css";
import { useEffect, useState, useCallback, useMemo } from "react";
import { Form } from "react-bootstrap";
import Calendar from "react-calendar";
import { AgGridReact } from "ag-grid-react";
import { useNavigate } from "react-router-dom";
import DatePicker from "react-datepicker";
import ButtonCom from "../../components/button/button";
// import { ToastContainer, toast } from "react-toastify";
import Dropdown from "react-bootstrap/Dropdown";
import { useGlobalToast } from '../../stopOldToastGlobally.js';
import { RxCross2 } from "react-icons/rx";
import { MdClear } from 'react-icons/md';
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Select from "react-select";
import makeAnimated from "react-select/animated";
import TableSkeleton from "../../components/table/TableSkeleton.js";

import CustomPagination from "../../components/CustomPagination";

import {
  getCapitalizeWord,
  getCurrentDate,
  getAttendanceFormattedData,
  downloadCSV,
  getInitials,
  getInitialColor,
  getDateWithMonthName,
  generateTimeSlots,
  formatHeader
} from "../../services/commonServices";
import {
  fetchBranchData,
  initBranchState,
} from "../../redux/slices/branchSlice";
import {
  fetchDepartmentData,
  initDepartState,
} from "../../redux/slices/departmentSlice";
import {
  fetchData as fetchShiftData,
  initShiftState,
} from "../../redux/slices/shiftsSlice";
import {
  fetchDailyAttData,
  initDailyAttState,
} from "../../redux/slices/dailyAttendanceSlice";
import {
  commonSaveData,
  initCommonState,
} from "../../redux/slices/commonSlice";
import { useDispatch, useSelector } from "react-redux";
import { dailyAttendancePath, editAttendancePath } from "../../services/config";

import { HasPermission } from "../../services/hasPermission";
import AttendanceSummary from "../../components/InfoCard/attendanceInfoCard";

const animatedComponents = makeAnimated();

// ============================================
// THEME-AWARE COLOR HELPER
// Reads data-theme attribute live — works for both light and dark
// ============================================
const getThemeColors = () => {
  // Read from localStorage — most reliable source
  const theme = localStorage.getItem("theme") ||
    localStorage.getItem("appTheme") ||
    localStorage.getItem("color-theme") ||
    localStorage.getItem("seeknow-theme") ||
    localStorage.getItem("mode");

  const isDark = theme === "dark" ||
    document.documentElement.classList.contains("dark") ||
    document.body.classList.contains("dark") ||
    document.documentElement.getAttribute("data-theme") === "dark" ||
    document.body.getAttribute("data-theme") === "dark";

  return {
    bgPage: isDark ? "#121425" : "#F5F7FA",
    bgCard: isDark ? "#19152C" : "#FFFFFF",
    bgSearch: isDark ? "#161421" : "#FFFFFF",
    bgTableHdr: isDark ? "#171522" : "#E9ECF5",
    bgRowOdd: isDark ? "#100D1B" : "#FFFFFF",
    bgRowEven: isDark ? "#131022" : "#F8FAFF",
    bgNav: isDark ? "#0C0A18" : "#F0F0FF",
    textPrimary: isDark ? "#E3E2EB" : "#1A1A2E",
    textMuted: isDark ? "#9998A2" : "#6B6B8A",
    textWhite: "#FFFFFF",
    primary: isDark ? "#9070F9" : "#6C63FF",
    primaryBtn: isDark ? "#8862F5" : "#6C63FF",
    primaryHover: isDark ? "#7550E8" : "#5A52D5",
    badgeActive: "#37AC7E",
    badgeInactive: "#D09E1B",
    success: "#22C55E",
    warning: "#F59E0B",
    danger: "#EF4444",
    chartGreen: "#42AC8A",
    chartYellow: "#F2C659",
    chartRed: "#E55E70",
    donutBlue: "#5F77E1",
    donutGreen: "#3EAC86",
    donutPurple: "#A78DED",
    donutYellow: "#D7B23C",
    borderDefault: isDark ? "#2D2A45" : "#E0E0F0",
    borderInput: isDark ? "#3D3A58" : "#E0E0F0",
  };
};

// ============================================
// STYLE CONSTANTS (non-color)
// ============================================
const TYPOGRAPHY = {
  nav: '13px',
  tab: '12px',
  h1: '18px',
  h2: '14px',
  h3: '12px',
  tableHeader: '12px',
  tableCell: '13px',
  employeeId: '12px',
  kpiNumber: '28px',
  kpiDenom: '18px',
  kpiLabel: '12px',
  badge: '11px',
  button: '13px',
  input: '12px',
  placeholder: '12px',
  caption: '10px',
  chartAxis: '10px',
  chartLegend: '10px',
  donutPercent: '11px',
};

const SPACING = {
  xs: '4px',
  sm: '8px',
  md: '12px',
  lg: '16px',
  xl: '20px',
  xxl: '24px',
  xxxl: '32px',
};

const RADIUS = {
  sm: '4px',
  default: '8px',
  card: '12px',
  pill: '20px',
  avatar: '50%',
};

const HEIGHTS = {
  nav: '48px',
  subtab: '44px',
  tableHdr: '44px',
  tableRow: '52px',
  input: '38px',
  badge: '22px',
};

// Status badge map — semantic colors, identical in both themes
const STATUS_COLOR_MAP = {
  "1": { bg: '#37AC7E', text: '#fff' },
  "9": { bg: '#37AC7E', text: '#fff' },
  "2": { bg: '#EF4444', text: '#fff' },
  "3": { bg: '#F59E0B', text: '#fff' },
  "4": { bg: '#D09E1B', text: '#fff' },
  "5": { bg: '#42AC8A', text: '#fff' },
  "6": { bg: '#A78DED', text: '#fff' },
  "7": { bg: '#D09E1B', text: '#fff' },
  "8": { bg: '#F59E0B', text: '#fff' },
  "10": { bg: '#F2C659', text: '#fff' },
};

// ============================================
// REACT-SELECT STYLES — reads live theme
// ============================================
const getCustomSelectStyles = () => {
  const C = getThemeColors();
  return {
    menuPortal: (base) => ({ ...base, zIndex: 9999, width: "100%" }),
    control: (provided) => ({
      ...provided,
      border: `1px solid ${C.borderInput}`,
      boxShadow: "none",
      backgroundColor: C.bgSearch,
      minHeight: "45px",
      borderRadius: RADIUS.default,
      cursor: "pointer",
      '&:hover': { borderColor: C.primary },
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isFocused
        ? C.bgNav
        : state.isSelected
          ? C.primary
          : C.bgCard,
      color: state.isSelected ? "#fff" : C.textPrimary,
      fontSize: TYPOGRAPHY.input,
      cursor: "pointer",
      padding: "10px 12px",
      '&:active': { backgroundColor: C.primary },
    }),
    menu: (provided) => ({
      ...provided,
      // backgroundColor: C.bgCard,
      border: `1px solid ${C.borderDefault}`,
      borderRadius: RADIUS.default,
      maxHeight: "250px",
      overflow: "hidden",
      zIndex: 9999,
      boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
    }),
    menuList: (provided) => ({
      ...provided,
      maxHeight: "250px",
      overflow: "auto",
      padding: "0",
      '&::-webkit-scrollbar': { width: "6px" },
      '&::-webkit-scrollbar-track': { background: C.bgNav },
      '&::-webkit-scrollbar-thumb': { background: C.borderInput, borderRadius: "3px" },
    }),
    singleValue: (provided) => ({ ...provided, color: C.textPrimary, fontSize: TYPOGRAPHY.input }),
    placeholder: (provided) => ({ ...provided, color: C.textMuted, fontSize: TYPOGRAPHY.placeholder }),
    input: (provided) => ({ ...provided, color: C.textPrimary, fontSize: TYPOGRAPHY.input }),
    dropdownIndicator: (provided) => ({
      ...provided, color: C.textMuted, padding: "0 8px",
      '&:hover': { color: C.textPrimary },
    }),
    indicatorSeparator: (provided) => ({ ...provided, backgroundColor: C.borderInput }),
    valueContainer: (provided) => ({ ...provided, padding: "2px 12px" }),
  };
};

// ============================================
// COLUMN DEFINITIONS — built OUTSIDE component
// Uses CSS classes .cell-text-primary / .cell-text-muted
// so text color always reflects the current theme even when
// AG Grid recycles cells without re-running cell renderers.
// ============================================
const buildColumnDefs = (handleShowAttendance, showImage) => [
  {
    headerName: "S.No",
    width: 70,
    pinned: "left",
    headerClass: 'ag-header-custom text-center',
    cellClass: 'text-center',
    cellRenderer: (params) => {
      const { node, api } = params;
      const currentPage = (api && api.paginationGetCurrentPage) ? api.paginationGetCurrentPage() : 0;
      const pageSize = (api && api.paginationGetPageSize) ? api.paginationGetPageSize() : (params?.context?.pageSize || 10);
      const serial = currentPage * (pageSize || 10) + (node?.rowIndex ?? 0) + 1;
      return (
        <div style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{serial}</div>
      );
    },
  },
  {
    headerName: "Employee ID",
    field: "employeeId",
    width: 130,
    pinned: "left",
    headerClass: 'ag-header-custom',
    cellRenderer: ({ data }) => (
      <span className="cell-text-muted" style={{
        fontSize: TYPOGRAPHY.employeeId,
        fontWeight: "500",
        fontFamily: "monospace",
      }}>
        {data.employeeId || "-"}
      </span>
    ),
    cellStyle: { display: "flex", alignItems: "center", justifyContent: "flex-start" },
  },
  {
    headerName: "Staff Name",
    field: "staffName",
    width: 220,
    pinned: "left",
    headerClass: 'ag-header-custom',
    cellRenderer: ({ data }) => {
      const staffName = data.staffName || "Unknown";
      return (
        <div
          style={{
            display: "flex", alignItems: "center", gap: SPACING.sm,
            cursor: "pointer", height: "100%", width: "100%",
          }}
          onClick={() => handleShowAttendance({ data })}
        >
          <div style={{
            width: "32px", height: "32px", borderRadius: RADIUS.avatar,
            display: "flex", alignItems: "center", justifyContent: "center",
            backgroundColor: getInitialColor(staffName),
            color: "#FFFFFF", fontWeight: "600", fontSize: TYPOGRAPHY.badge,
            flexShrink: 0,
          }}>
            {getInitials(staffName)}
          </div>
          <span className="cell-text-primary" style={{
            fontSize: TYPOGRAPHY.tableCell,
            fontWeight: "500",
            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
          }}>
            {staffName}
          </span>
        </div>
      );
    },
    cellStyle: { display: "flex", alignItems: "center" },
  },
  {
    headerName: "Designation",
    field: "designation",
    width: 155,
    headerClass: 'ag-header-custom',
    cellRenderer: ({ data }) => (
      <span className="cell-text-primary" style={{
        fontSize: TYPOGRAPHY.tableCell,
        whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
      }}>
        {data.designation || "-"}
      </span>
    ),
    cellStyle: { display: "flex", alignItems: "center" },
  },
  {
    headerName: "Branch",
    field: "branchName",
    width: 160,
    headerClass: 'ag-header-custom',
    cellRenderer: ({ data }) => (
      <span className="cell-text-primary" style={{
        fontSize: TYPOGRAPHY.tableCell,
        whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
      }}>
        {data.branchName || "-"}
      </span>
    ),
    cellStyle: { display: "flex", alignItems: "center" },
  },
  {
    headerName: "Check In Image",
    field: "clockInImage",
    width: 120,
    headerClass: 'ag-header-custom',
    cellRenderer: ({ data }) =>
      data.clockInImage ? (
        <div
          style={{
            backgroundColor: getInitialColor(data.staffName || "Unknown"),
            width: "36px", height: "36px", borderRadius: RADIUS.avatar,
            overflow: "hidden", cursor: "pointer",
          }}
          onClick={() => showImage(data.clockInImage)}
        >
          <img
            src={data.clockInImage}
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
            alt="check-in"
          />
        </div>
      ) : (
        <span className="cell-text-muted" style={{ fontSize: TYPOGRAPHY.tableCell }}>-</span>
      ),
    cellStyle: { display: "flex", alignItems: "center" },
  },
  {
    headerName: "First In",
    field: "checkInTime",
    width: 100,
    headerClass: 'ag-header-custom',
    cellRenderer: ({ data }) => (
      <span className="cell-text-primary" style={{ fontSize: TYPOGRAPHY.tableCell }}>
        {data.checkInTime || "-"}
      </span>
    ),
    cellStyle: { display: "flex", alignItems: "center" },
  },
  {
    headerName: "Check In Location",
    field: "checkInLocation",
    width: 150,
    headerClass: 'ag-header-custom',
    cellRenderer: ({ data }) => (
      <span className="cell-text-primary" style={{
        fontSize: TYPOGRAPHY.tableCell,
        whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
      }}>
        {data.checkInLocation || "-"}
      </span>
    ),
    cellStyle: { display: "flex", alignItems: "center" },
  },
  {
    headerName: "Shift Start Time",
    field: "shiftStartTime",
    width: 120,
    headerClass: 'ag-header-custom',
    cellRenderer: ({ data }) => (
      <span className="cell-text-primary" style={{ fontSize: TYPOGRAPHY.tableCell }}>
        {data.shiftStartTime || "-"}
      </span>
    ),
    cellStyle: { display: "flex", alignItems: "center" },
  },
  {
    headerName: "Check Out Image",
    field: "clockOutImage",
    width: 120,
    headerClass: 'ag-header-custom',
    cellRenderer: ({ data }) =>
      data.clockOutImage ? (
        <div
          style={{
            backgroundColor: getInitialColor(data.staffName || "Unknown"),
            width: "36px", height: "36px", borderRadius: RADIUS.avatar,
            overflow: "hidden", cursor: "pointer",
          }}
          onClick={() => showImage(data.clockOutImage)}
        >
          <img
            src={data.clockOutImage}
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
            alt="check-out"
          />
        </div>
      ) : (
        <span className="cell-text-muted" style={{ fontSize: TYPOGRAPHY.tableCell }}>-</span>
      ),
    cellStyle: { display: "flex", alignItems: "center" },
  },
  {
    headerName: "Last Out",
    field: "checkOutTime",
    width: 100,
    headerClass: 'ag-header-custom',
    cellRenderer: ({ data }) => (
      <span className="cell-text-primary" style={{ fontSize: TYPOGRAPHY.tableCell }}>
        {data.checkOutTime || "-"}
      </span>
    ),
    cellStyle: { display: "flex", alignItems: "center" },
  },
  {
    headerName: "Check Out Location",
    field: "checkOutLocation",
    width: 150,
    headerClass: 'ag-header-custom',
    cellRenderer: ({ data }) => (
      <span className="cell-text-primary" style={{
        fontSize: TYPOGRAPHY.tableCell,
        whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
      }}>
        {data.checkOutLocation || "-"}
      </span>
    ),
    cellStyle: { display: "flex", alignItems: "center" },
  },
  {
    headerName: "Shift End Time",
    field: "shiftEndTime",
    width: 120,
    headerClass: 'ag-header-custom',
    cellRenderer: ({ data }) => (
      <span className="cell-text-primary" style={{ fontSize: TYPOGRAPHY.tableCell }}>
        {data.shiftEndTime || "-"}
      </span>
    ),
    cellStyle: { display: "flex", alignItems: "center" },
  },
  {
    headerName: "Hours Worked",
    field: "totalHours",
    width: 110,
    headerClass: 'ag-header-custom',
    cellRenderer: ({ data }) => (
      <span className="cell-text-primary" style={{ fontSize: TYPOGRAPHY.tableCell }}>
        {data.totalHours || "-"}
      </span>
    ),
    cellStyle: { display: "flex", alignItems: "center" },
  },
  {
    headerName: "Status",
    field: "status",
    width: 130,
    pinned: "right",
    headerClass: 'ag-header-custom',
    cellRenderer: ({ data }) => {
      const { bg = 'transparent', text = '#333' } =
        STATUS_COLOR_MAP[data.statusCode] || {};
      return (
        <div style={{
          textAlign: "center",
          height: "28px", lineHeight: "28px",
          borderRadius: "6px",
          padding: `0 ${SPACING.sm}`,
          backgroundColor: bg,
          color: text,
          fontSize: TYPOGRAPHY.badge,
          fontWeight: "600",
          display: "inline-block",
          minWidth: "90px",
          whiteSpace: "nowrap",
        }}>
          {formatHeader(data.status || "") || "-"}
        </div>
      );
    },
    cellStyle: { display: "flex", alignItems: "center", justifyContent: "center" },
  },
];

// ============================================
// MAIN COMPONENT
// ============================================
const DailyAttendance = () => {
  const customToast = useGlobalToast(); 
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = JSON.parse(localStorage.getItem("user_data")) || null;

  const branchState = useSelector((state) => state.branch);
  const departmentState = useSelector((state) => state.department);
  const shiftState = useSelector((state) => state.shift);
  const dailyAttendanceState = useSelector((state) => state.dailyAttendance);
  const commonStore = useSelector((state) => state.common);

  const [currentDate, setCurrentDate] = useState(getCurrentDate());
  const [branchId, setBranchId] = useState("");
  const [shiftId, setShiftId] = useState("");
  const [departId, setDepartId] = useState("");
  const [totalStaffCount, setTotalStaffCount] = useState("");
  const [dailySummary, setDailySummary] = useState({});
  const [dailyAttendance, setDailyAttendance] = useState([]);
  const [filteredAttendance, setFilteredAttendance] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [branchList, setBranchList] = useState([]);
  const [departmentList, setDepartmentList] = useState([]);
  const [shiftList, setShiftList] = useState([]);
  const [isShow, setIsShow] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [open, setOpen] = useState(false);
  const [showAttendanceType, setShowAttendanceType] = useState(false);
  const [filterByValue, setFilterByValue] = useState("");
  const [editAttValue, setEditAttValue] = useState("");
  const [editLeaveValue, setEditLeaveValue] = useState("");
  const [editPunchIn, setEditPunchIn] = useState("");
  const [editPunchOut, setEditPunchOut] = useState("");
  const [attendanceType, setAttendanceType] = useState({});
  const [selectedTime, setSelectedTime] = useState("");
  const [selectedTimeArray, setSelectedTimeArray] = useState([]);
  const [timeList, setTimeList] = useState([]);
  const [value, setValue] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [gridApi, setGridApi] = useState(null);

  // Live theme colors — for dialogs, drawer, and non-grid UI only
  const [themeColors, setThemeColors] = useState(getThemeColors());

  // Watch theme changes → update UI colors for dialogs/drawer
  // Watch theme changes → watches class AND data-theme on BOTH html AND body
  useEffect(() => {
    const refresh = () => setThemeColors(getThemeColors());

    // Watch DOM changes
    const observer = new MutationObserver(refresh);
    const opts = { attributes: true, attributeFilter: ["class", "data-theme"] };
    observer.observe(document.documentElement, opts);
    observer.observe(document.body, opts);

    // Watch localStorage changes (when theme toggle saves to localStorage)
    window.addEventListener("storage", refresh);

    return () => {
      observer.disconnect();
      window.removeEventListener("storage", refresh);
    };
  }, []);

  const onGridReady = (params) => setGridApi(params.api);
  const maxMonth = new Date();
  const today = getCurrentDate();

  // Live theme shorthand for JSX (dialogs, drawer, etc.)
  const C = themeColors;

  // ── Data Fetching ────────────────────────────────────────────────────────────
  const getAttendance = useCallback((date, bId, sId, dId) => {
    if (!user?.user_id) return;
    dispatch(
      fetchDailyAttData({
        path: dailyAttendancePath,
        data: {
          userId: user.user_id,
          selectedDate: date,
          branchId: bId || "",
          shiftId: sId || "",
          departmentId: dId || "",
        },
      })
    );
  }, [dispatch, user?.user_id]);

  // Initial fetch
  useEffect(() => {
    if (user?.user_id) {
      getAttendance(currentDate, branchId, shiftId, departId);
      getFilters();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-fetch when filters change
  useEffect(() => {
    if (user?.user_id) {
      getAttendance(currentDate, branchId, shiftId, departId);
    }
  }, [branchId, shiftId, departId, currentDate, getAttendance, user?.user_id]);

  const handleAttChange = ({ target: { value, name } }) => {
    if (name === "shiftId") setShiftId(value);
    else if (name === "branchId") setBranchId(value);
    else if (name === "departId") setDepartId(value);
  };

  const getFilters = useCallback(() => {
    if (!user?.user_id) return;
    dispatch(fetchBranchData(user.user_id));
    dispatch(fetchDepartmentData(user.user_id));
    dispatch(fetchShiftData(user.user_id));
  }, [dispatch, user?.user_id]);

  // Time slots generation
  useEffect(() => {
    const generateTimeSlotsList = () => {
      const slots = [];
      for (let hour = 0; hour < 24; hour++) {
        for (let minute = 0; minute < 60; minute += 30) {
          slots.push(
            `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`
          );
        }
      }
      return slots;
    };

    let slots = [];
    try {
      slots = generateTimeSlots();
      if (slots && slots.length > 0) {
        slots = slots.map(time => {
          if (time && time.includes(':')) {
            const parts = time.split(':');
            if (parts.length >= 2) return `${parts[0]}:${parts[1]}`;
          }
          return time;
        });
      } else {
        slots = generateTimeSlotsList();
      }
    } catch (error) {
      slots = generateTimeSlotsList();
    }

    const validSlots = slots.filter(time => time && time.includes(':'));
    const uniqueSlots = [...new Set(validSlots)].sort();
    setTimeList(uniqueSlots.map(row => ({ label: row, value: row })));
  }, []);

  const handleTimeChange = (selected) => {
    setSelectedTimeArray(selected);
    setSelectedTime(selected ? selected.value : "");
  };

  // ── Redux state handler ──────────────────────────────────────────────────────
  useEffect(() => {
    if (branchState.brnGetSuccess) {
      setBranchList(branchState.brnGetData || []);
      dispatch(initBranchState());
    }
    if (departmentState.depGetSuccess) {
      setDepartmentList(departmentState.depGetData || []);
      dispatch(initDepartState());
    }
    if (shiftState.shiftGetSuccess) {
      setShiftList(shiftState.shiftGetData || []);
      dispatch(initShiftState());
    }

    if (dailyAttendanceState.dailyAttGetSuccess) {
      const { status, data } = dailyAttendanceState.dailyAttGetData || {};
      if (status === true || status === "true") {
        const attendanceList = data?.attendanceList || [];
        setTotalStaffCount(data?.totalStaffCount || 0);
        setDailySummary(data?.dailySummary || {});
        setDailyAttendance(attendanceList);
        if (searchQuery) {
          searchEmployees(searchQuery, attendanceList);
        } else {
          setFilteredAttendance(attendanceList);
        }
      } else {
        setDailySummary({});
        setDailyAttendance([]);
        setFilteredAttendance([]);
        setTotalStaffCount(0);
      }
      dispatch(initDailyAttState());
    }

    if (dailyAttendanceState.dailyAttGetError) {
      setTotalStaffCount("");
      setDailySummary({});
      setDailyAttendance([]);
      setFilteredAttendance([]);
      dispatch(initDailyAttState());
    }

    if (commonStore.commonSuccess) {
    customToast.success(commonStore.commonMessage || "Saved successfully");
      setEditAttValue("");
      setEditLeaveValue("");
      setAttendanceType({});
      setShowAttendanceType(false);
      setOpen(false);
      setSelectedTime("");
      setSelectedTimeArray([]);
      getAttendance(currentDate, branchId, shiftId, departId);
      dispatch(initCommonState());
    }

    if (commonStore.commonError) {
customToast.error(commonStore.commonMessage || "Something went wrong");
      setShowAttendanceType(false);
      setOpen(false);
      setSelectedTime("");
      setSelectedTimeArray([]);
      dispatch(initCommonState());
    }
  }, [
    branchState,
    departmentState,
    shiftState,
    dailyAttendanceState,
    commonStore,
    dispatch,
    searchQuery,
    currentDate,
    branchId,
    shiftId,
    departId,
    getAttendance,
  ]);

  // ── Event Handlers ───────────────────────────────────────────────────────────
  const goToEmployeeAttendance = (data) => {
    localStorage.setItem("user-name", data.data.staffName);
    navigate(`employee-attendance?staffId=${data.data.staffId}`);
  };

  const handleShowAttendance = useCallback((data) => {
    setEditPunchIn(data.data.checkInTime || "");
    setEditPunchOut(data.data.checkOutTime || "");
    setAttendanceType(data.data);
    setEditAttValue(data.data.statusCode || "");
    setShowAttendanceType(prev => !prev);
  }, []);

  const handleShowHide = () => {
    if (showAttendanceType) setShowAttendanceType(false);
  };

  const handleTimeClose = () => setOpen(false);

  const savePunchTime = (t1, t2) => {
    if (!attendanceType.staffId || !attendanceType.date) return;
    dispatch(commonSaveData({
      path: editAttendancePath,
      data: {
        userId: user.user_id,
        selectedDate: attendanceType.date,
        staffId: attendanceType.staffId,
        attendanceType: editAttValue || "1",
        punchOut: t2 || "",
        punchIn: t1 || "",
      },
    }));
  };

  const handleAddTimeChange = () => {
    if (!selectedTime) { customToast.error("Please select a time"); return; }
    if (editPunchIn) {
      savePunchTime(editPunchIn, selectedTime);
    } else {
      savePunchTime(selectedTime, "");
    }
  };

  const showImage = useCallback((url) => {
    if (!url) return;
    const link = document.createElement("a");
    link.href = url;
    link.target = "blank_";
    link.click();
  }, []);

  // ── Stable column defs — rebuilt only if callbacks change (they won't) ───────
  const columnDefs = useMemo(
    () => buildColumnDefs(handleShowAttendance, showImage),
    [handleShowAttendance, showImage]
  );

  // ── CSV Download ─────────────────────────────────────────────────────────────
  const DownloadCSVFile = () => {
    if (dailyAttendance.length) {
      downloadCSV(
        getAttendanceFormattedData(dailyAttendance),
        `${currentDate}_attendance.csv`
      );
      customToast.success("Attendance report downloaded successfully!");
    } else {
     customToast.warning("No data to export");
    }
  };

  const paginationPageSize = 10;
  const paginationPageSizeSelector = [10, 20, 50, 100];

  // ── Date / Filter Helpers ────────────────────────────────────────────────────
  const handleDateChange = (val) => {
    setIsOpen(false);
    if (val) {
      const d = new Date(val);
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      setCurrentDate(`${year}-${month}-${day}`);
    } else {
     customToast.error("Invalid date selected");
    }
  };

  const resetData = () => {
    setCurrentDate(getCurrentDate());
    setBranchId("");
    setShiftId("");
    setDepartId("");
    setSearchQuery("");
  };

  const filterUserAccordingStatus = (value) => {
    setFilterByValue(value);
    const filteredEmployees = dailyAttendance.filter(
      (v) => v.status?.toLowerCase().trim() === value.toLowerCase().trim()
    );
    setFilteredAttendance(filteredEmployees);
  };

  const filterUserAccordingStatusMenu = () => setIsShow(true);

  const handleClose = () => setIsShow(false);

  const filterUserAccordingStatusOne = (value) => setFilterByValue(value);

  const filterEmployee = () => {
    const filteredEmployees = dailyAttendance?.filter(
      (v) => v.status?.toLowerCase().trim() === filterByValue.toLowerCase().trim()
    ) || [];
    setFilteredAttendance(filteredEmployees);
    setIsShow(false);
  };

  const editAttendance = (type) => setEditAttValue(type);

  const saveAttendance = () => {
    if (!attendanceType.staffId || !attendanceType.date) return;
    dispatch(commonSaveData({
      path: editAttendancePath,
      data: {
        userId: user.user_id,
        selectedDate: attendanceType.date,
        staffId: attendanceType.staffId,
        attendanceType: editAttValue,
      },
    }));
  };

  const editLeaves = (type) => {
    setEditLeaveValue(type);
    if (!attendanceType.staffId || !attendanceType.date) return;
    dispatch(commonSaveData({
      path: editAttendancePath,
      data: {
        userId: user.user_id,
        selectedDate: attendanceType.date,
        staffId: attendanceType.staffId,
        attendanceType: type,
      },
    }));
  };

  const searchEmployees = (val, dataList) => {
    setSearchQuery(val);
    const listToSearch = dataList || dailyAttendance;
    const filteredEmployees = listToSearch?.filter(
      (v) =>
        (v.staffName && v.staffName.toLowerCase().includes(val.toLowerCase())) ||
        (v.employeeId && v.employeeId.toLowerCase().includes(val.toLowerCase()))
    ) || [];
    setFilteredAttendance(filteredEmployees);
  };

  const handleDateSelection = (date) => {
    setValue(date);
    setSelectedDate(date);
  };

  const tileClassName = ({ date, view }) => {
    if (view === "month") {
      const isSameMonth =
        date.getMonth() + 1 === value.getMonth() + 1 &&
        date.getFullYear() === value.getFullYear();

      const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
      const match = dailyAttendance.find((item) => item.dates === dateStr);

      if (isSameMonth) {
        return match
          ? `dot-${match?.status} highlight-current-month`
          : "highlight-current-month";
      } else {
        return "highlight-other-month";
      }
    }
    return null;
  };

  const clearFilter = () => {
    setSearchQuery("");
    setBranchId("");
    setShiftId("");
    setDepartId("");
    setCurrentDate(getCurrentDate());
  };

  // ── RENDER ───────────────────────────────────────────────────────────────────
  return (
    <>


      <style>{`
      .attendance-calendar-wrapper .react-calendar {
        background-color: ${C.bgCard} !important;
        border-color: ${C.borderDefault} !important;
      }
      .attendance-calendar-wrapper .react-calendar__navigation button {
        color: ${C.textPrimary} !important;
        background: transparent !important;
      }
      .attendance-calendar-wrapper .react-calendar__navigation__label {
        color: ${C.textPrimary} !important;
      }
      .attendance-calendar-wrapper .react-calendar__month-view__weekdays__weekday {
        color: ${C.textMuted} !important;
      }
      .attendance-calendar-wrapper .react-calendar__month-view__days__day {
        color: ${C.textPrimary} !important;
        background: transparent !important;
      }
      .attendance-calendar-wrapper .react-calendar__month-view__days__day--neighboringMonth {
        color: ${C.textMuted} !important;
        opacity: 0.4 !important;
      }
      .attendance-calendar-wrapper .react-calendar__tile:enabled:hover {
        background: rgba(134, 96, 248, 0.15) !important;
      }
    `}</style>
      <div className="mt-0">
       
       

        {/* Summary Cards */}
        <div className="d-flex align-items-center justify-content-start gap-2 mt-2">
          <AttendanceSummary
            color="green-bg-1"
            label="Present Employees"
            value={dailySummary?.present ?? 0}
            total={totalStaffCount}
          />
          <AttendanceSummary
            color="red-bg-1"
            label="Absent Employees"
            value={dailySummary?.absent ?? 0}
            total={totalStaffCount}
          />
          <AttendanceSummary
            color="lavender-bg-1"
            label="Half Day"
            value={dailySummary?.halfDay ?? 0}
            total={totalStaffCount}
          />
        </div>

        {/* Filters */}
        <Form className="d-flex align-items-center justify-content-start gap-3 mt-2">
          <Form.Control
            placeholder="Search by Employee Name & ID"
            value={searchQuery}
            onChange={(e) => searchEmployees(e.target.value, dailyAttendance)}
            className="universal-input-search"
            style={{ width: "250px" }}
          />

          <div style={{ width: "200px" }}>
            <DatePicker
              id="datePicker"
              selected={currentDate ? new Date(currentDate) : null}
              onChange={handleDateChange}
              dateFormat="yyyy-MM-dd"
              maxDate={maxMonth}
              onClickOutside={() => setIsOpen(false)}
              onInputClick={() => setIsOpen(true)}
              open={isOpen}
              className="universal-input-one"
              popperClassName="custom-datepicker-popper"
              portalId="root-portal"
            />
          </div>

          <Form.Select
            value={branchId}
            name="branchId"
            onChange={handleAttChange}
            className="universal-input-one"
            style={{ width: "200px" }}
          >
            <option value="">Select Branch</option>
            {branchList.map((branch) => (
              <option key={branch.branchId} value={branch.branchId}>
                {branch.branch_name}
              </option>
            ))}
          </Form.Select>

          <Form.Select
            value={shiftId}
            name="shiftId"
            onChange={handleAttChange}
            className="universal-input-one"
            style={{ width: "200px" }}
          >
            <option value="">Select Shifts</option>
            {shiftList.map((shift) => (
              <option key={shift.shiftId} value={shift.shiftId}>
                {shift.shiftName}
              </option>
            ))}
          </Form.Select>

          <Form.Select
            value={departId}
            name="departId"
            onChange={handleAttChange}
            className="universal-input-one"
            style={{ width: "200px" }}
          >
            <option value="">Select Department</option>
            {departmentList.map((dep) => (
              <option key={dep.departmentId} value={dep.departmentId}>
                {dep.department_name}
              </option>
            ))}
          </Form.Select>

          <Form.Group>
            {(searchQuery || currentDate !== today || shiftId || departId || branchId) && (
              <button type="button" className="clear" onClick={clearFilter}>
                <MdClear /> Clear
              </button>
            )}
          </Form.Group>
        </Form>

        {/* AG Grid Table */}
        <div
          className="ag-theme-quartz custom-grid mt-2"
          style={{
            height: "calc(100vh - 320px)",
            width: "100%",
            maxWidth: "100%",
            overflow: "hidden",
            boxSizing: "border-box",
          }}
          onClick={handleShowHide}
        >
          {dailyAttendanceState.dailyAttGetLoading ? (
            <TableSkeleton />
          ) : (
            <AgGridReact
              rowData={Array.isArray(filteredAttendance) ? filteredAttendance : []}
              columnDefs={columnDefs}
              pagination={true}
              paginationPageSize={paginationPageSize}
              paginationPageSizeSelector={paginationPageSizeSelector}
              onGridReady={onGridReady}
              rowHeight={parseInt(HEIGHTS.tableRow)}
              headerHeight={parseInt(HEIGHTS.tableHdr)}
              defaultColDef={{
                cellStyle: { display: "flex", alignItems: "center" },
                resizable: true,
                sortable: true,
                minWidth: 80,
                wrapHeaderText: false,
                autoHeaderHeight: false,
              }}
              suppressCellFocus={true}
              animateRows={true}
              domLayout="normal"
              suppressHorizontalScroll={false}
              enableCellTextSelection={true}
              ensureDomOrder={true}
              suppressColumnVirtualisation={false}
              alwaysShowHorizontalScroll={false}
            />
          )}
        </div>

        {/* Pagination + Download */}
        <div className="d-flex align-items-center justify-content-between custom-pagination">
          {gridApi && <CustomPagination gridApi={gridApi} />}
          {HasPermission("Daily Attendance", "export") && (
            <ButtonCom
              type={"button"}
              class_name={"p_btn universal-btn height_35"}
              text="Download Report"
              url_or_onclick={DownloadCSVFile}
              icon={null}
              width={"200px"}
            />
          )}
        </div>

        {/* Filter by Status Dialog */}
        <Dialog
          open={isShow}
          onClose={handleClose}
          fullWidth={true}
          maxWidth={"sm"}
          style={{ overflow: "visible" }}
          className="dark-bg-color"
          PaperProps={{
            style: {
              backgroundColor: C.bgCard,
              borderRadius: RADIUS.card,
            },
          }}
        >

          <style>{`
    .attendance-calendar-wrapper .react-calendar,
    .attendance-calendar-wrapper .react-calendar * {
      background-color: transparent !important;
      background: transparent !important;
    }
    .attendance-calendar-wrapper .react-calendar {
      border: none !important;
    }
    .attendance-calendar-wrapper .react-calendar__navigation button {
      color: ${C.textPrimary} !important;
    }
    .attendance-calendar-wrapper .react-calendar__navigation__label {
      color: ${C.textPrimary} !important;
    }
    .attendance-calendar-wrapper .react-calendar__month-view__weekdays__weekday {
      color: ${C.textMuted} !important;
    }
    .attendance-calendar-wrapper .react-calendar__month-view__days__day {
      color: ${C.textPrimary} !important;
    }
    .attendance-calendar-wrapper .react-calendar__month-view__days__day--neighboringMonth {
      color: ${C.textMuted} !important;
      opacity: 0.35 !important;
    }
    .attendance-calendar-wrapper .react-calendar__tile--active,
    .attendance-calendar-wrapper .react-calendar__tile--active:enabled:hover {
      background: linear-gradient(135deg, #8660F8, #6C63FF) !important;
      color: #fff !important;
    }
    .attendance-calendar-wrapper .react-calendar__tile--now:not(.react-calendar__tile--active) {
      background: rgba(134,96,248,0.12) !important;
      color: ${C.primary} !important;
    }
    .attendance-calendar-wrapper .react-calendar__tile:enabled:hover {
      background: rgba(134,96,248,0.12) !important;
      color: ${C.primary} !important;
    }
  `}</style>
          <DialogTitle
            id="alert-dialog-title"
            style={{
              color: C.textPrimary,
              fontSize: TYPOGRAPHY.h2,
              fontWeight: "600",
              marginBottom: SPACING.md,
            }}
          >
            {"Filter Employee by status"}
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              <div style={{
                display: "flex", flexWrap: "wrap",
                alignItems: "center", justifyContent: "flex-start",
                gap: SPACING.sm,
              }}>
                {[
                  { key: 'leave', label: 'Leave', count: dailySummary?.leave ?? 0 },
                  { key: 'lateCheckIn', label: 'Late Check In', count: dailySummary?.lateCheckIn ?? 0 },
                  { key: 'earlyCheckOut', label: 'Early Check Out', count: dailySummary?.earlyCheckOut ?? 0 },
                  { key: 'overtime', label: 'Overtime', count: dailySummary?.overtime ?? 0 },
                  { key: 'weekOff', label: 'WeekOff', count: dailySummary?.weekOff ?? 0 },
                  { key: 'holiday', label: 'Holiday', count: dailySummary?.holiday ?? 0 },
                  { key: 'Auto Clocked Out', label: 'Auto Clocked Out', count: dailySummary?.["Auto Clocked Out"] ?? 0 },
                  { key: 'lateCheckInEarlyCheckOut', label: 'Late In Early Out', count: dailySummary?.lateCheckInEarlyCheckOut ?? 0 },
                ].map((item) => (
                  <span
                    key={item.key}
                    onClick={() => filterUserAccordingStatusOne(item.key)}
                    style={{
                      backgroundColor: filterByValue === item.key ? C.primary : 'transparent',
                      color: filterByValue === item.key ? C.textWhite : C.textMuted,
                      border: `1px solid ${C.borderDefault}`,
                      borderRadius: RADIUS.pill,
                      padding: `${SPACING.xs} ${SPACING.sm}`,
                      cursor: "pointer",
                      fontSize: TYPOGRAPHY.badge,
                    }}
                  >
                    {item.label} ({item.count})
                  </span>
                ))}
              </div>
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleClose}
              style={{
                backgroundColor: 'transparent',
                color: C.textMuted,
                border: `1px solid ${C.borderDefault}`,
                borderRadius: RADIUS.default,
                padding: `${SPACING.xs} ${SPACING.lg}`,
                fontSize: TYPOGRAPHY.button,
                textTransform: 'none',
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={filterEmployee}
              style={{
                backgroundColor: C.primaryBtn,
                color: C.textWhite,
                border: 'none',
                borderRadius: RADIUS.default,
                padding: `${SPACING.xs} ${SPACING.lg}`,
                fontSize: TYPOGRAPHY.button,
                textTransform: 'none',
              }}
              autoFocus
            >
              Filter
            </Button>
          </DialogActions>
        </Dialog>

        {/* Select Time Dialog */}
        <Dialog
          fullWidth={true}
          maxWidth={"sm"}
          open={open}
          onClose={handleTimeClose}
          className="dark-bg-color"
          PaperProps={{
            style: {
              backgroundColor: C.bgCard,
              borderRadius: RADIUS.card,
              margin: "20px",
              maxHeight: "90vh",
            },
          }}
        >
          <DialogTitle style={{
            fontSize: TYPOGRAPHY.h2,
            fontWeight: "600",
            color: C.textPrimary,
            padding: "20px 24px",
            borderBottom: `1px solid ${C.borderDefault}`,
          }}>
            Select Time
          </DialogTitle>

          <DialogContent style={{ padding: "24px", overflow: "visible" }}>
            <Form.Group style={{ width: "100%" }}>


              <Select
                closeMenuOnSelect={true}
                value={selectedTimeArray}
                placeholder="Select time"
                options={timeList}
                onChange={handleTimeChange}
                styles={getCustomSelectStyles()}
                menuPlacement="auto"
                menuPortalTarget={document.body}
                maxMenuHeight={250}
                isClearable={false}
                isSearchable={true}
                backspaceRemovesValue={false}
                classNamePrefix="react-select"
              />
            </Form.Group>
          </DialogContent>

          <DialogActions style={{
            padding: "16px 24px 24px 24px",
            borderTop: `1px solid ${C.borderDefault}`,
            justifyContent: "flex-end",
            gap: "12px",
          }}>
            <Button
              onClick={handleTimeClose}
              style={{
                backgroundColor: 'transparent',
                color: C.textMuted,
                border: `1px solid ${C.borderDefault}`,
                borderRadius: RADIUS.default,
                padding: '8px 24px',
                fontSize: TYPOGRAPHY.button,
                minWidth: '100px',
                textTransform: 'none',
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddTimeChange}
              disabled={!selectedTime}
              style={{
                backgroundColor: selectedTime ? C.primaryBtn : C.borderDefault,
                color: C.textWhite,
                border: 'none',
                borderRadius: RADIUS.default,
                padding: '8px 24px',
                fontSize: TYPOGRAPHY.button,
                minWidth: '100px',
                cursor: selectedTime ? 'pointer' : 'not-allowed',
                textTransform: 'none',
                opacity: selectedTime ? 1 : 0.6,
              }}
            >
              Add
            </Button>
          </DialogActions>
        </Dialog>
      </div>

      {/* Edit Attendance Side Drawer */}
      <div
        className={showAttendanceType ? "date-hamberger active" : "date-hamberger"}
        style={{
          position: "fixed",
          right: "0",
          top: "0",
          height: "100vh",
          width: "400px",
          backgroundColor: C.bgCard,
          boxShadow: "-4px 0 20px rgba(0,0,0,0.15)",
          transform: showAttendanceType ? "translateX(0)" : "translateX(100%)",
          transition: "transform 0.3s ease",
          zIndex: 1000,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          visibility: showAttendanceType ? "visible" : "hidden",
          pointerEvents: showAttendanceType ? "auto" : "none",
        }}
      >
        {/* Drawer Header */}~
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "20px",
          borderBottom: `1px solid ${C.borderDefault}`,
          flexShrink: 0,
          backgroundColor: C.bgCard,
        }}>
          <div style={{ color: C.textPrimary, display: "flex", alignItems: "center" }}>
            <RxCross2
              style={{ fontSize: "18px", color: C.textPrimary, cursor: "pointer", marginRight: "10px" }}
              onClick={() => setShowAttendanceType(false)}
            />
            <span style={{ fontSize: TYPOGRAPHY.h3, color: C.textPrimary }}>Edit Attendance</span>
          </div>
          <span style={{ color: C.textPrimary, fontSize: TYPOGRAPHY.h3 }}>
            {getCapitalizeWord(attendanceType.staffName || "")}
          </span>
        </div>

        {/* Drawer Body */}
        <div style={{ padding: "20px", overflowY: "auto", flex: 1, backgroundColor: C.bgCard }}>

          {/* Calendar — wrapper uses theme bg, calendar itself themed via CSS */}
          <div
            className="attendance-calendar-wrapper"
            style={{
              marginBottom: "20px",
              padding: "10px",
              backgroundColor: C.bgCard,
              borderRadius: RADIUS.default,
              border: `1px solid ${C.borderDefault}`,
            }}
          >
            <Calendar
              onChange={handleDateSelection}
              value={value}
              tileClassName={tileClassName}
              calendarType="gregory"
            />
          </div>

          <h5 style={{ color: C.textPrimary, fontSize: TYPOGRAPHY.h3, marginBottom: "15px" }}>
            {currentDate && getDateWithMonthName(currentDate)}
          </h5>

          {/* Attendance Status Buttons */}
          <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", marginBottom: "20px" }}>
            <button
              onClick={() => editAttendance("2")}
              style={{
                backgroundColor: editAttValue === "2" ? C.danger : 'transparent',
                border: `1px solid ${C.danger}`,
                color: editAttValue === "2" ? C.textWhite : C.danger,
                borderRadius: "4px", padding: "8px 16px", cursor: "pointer",
                minWidth: "80px", textAlign: "center",
                fontSize: TYPOGRAPHY.badge, fontWeight: "600",
              }}
            >
              Absent
            </button>
            <button
              onClick={() => editAttendance("3")}
              style={{
                backgroundColor: editAttValue === "3" ? C.warning : 'transparent',
                border: `1px solid ${C.warning}`,
                color: editAttValue === "3" ? C.textWhite : C.warning,
                borderRadius: "4px", padding: "8px 16px", cursor: "pointer",
                minWidth: "80px", textAlign: "center",
                fontSize: TYPOGRAPHY.badge, fontWeight: "600",
              }}
            >
              HalfDay
            </button>
            <button
              onClick={() => editAttendance("4")}
              style={{
                backgroundColor: editAttValue === "4" ? C.badgeInactive : 'transparent',
                border: `1px solid ${C.badgeInactive}`,
                color: editAttValue === "4" ? C.textWhite : C.badgeInactive,
                borderRadius: "4px", padding: "8px 16px", cursor: "pointer",
                minWidth: "80px", textAlign: "center",
                fontSize: TYPOGRAPHY.badge, fontWeight: "600",
              }}
            >
              Weekoff
            </button>
            <button
              onClick={() => editAttendance("5")}
              style={{
                backgroundColor: editAttValue === "5" ? C.chartGreen : 'transparent',
                border: `1px solid ${C.chartGreen}`,
                color: editAttValue === "5" ? C.textWhite : C.chartGreen,
                borderRadius: "4px", padding: "8px 16px", cursor: "pointer",
                minWidth: "80px", textAlign: "center",
                fontSize: TYPOGRAPHY.badge, fontWeight: "600",
              }}
            >
              Holiday
            </button>

            <Dropdown>
              <Dropdown.Toggle
                id="dropdown-basic"
                style={{
                  backgroundColor: editAttValue === "1" || editAttValue === "9"
                    ? C.badgeActive : 'transparent',
                  border: `1px solid ${C.badgeActive}`,
                  color: editAttValue === "1" || editAttValue === "9"
                    ? C.textWhite : C.badgeActive,
                  borderRadius: "4px", padding: "8px 16px", cursor: "pointer",
                  minWidth: "80px", textAlign: "center",
                  fontSize: TYPOGRAPHY.badge, fontWeight: "600",
                }}
              >
                Present
              </Dropdown.Toggle>
              <Dropdown.Menu style={{
                backgroundColor: C.bgCard,
                border: `1px solid ${C.borderDefault}`,
                borderRadius: "4px",
              }}>
                <Dropdown.Item
                  onClick={() => editAttendance("1")}
                  style={{
                    color: editAttValue === "1" ? C.badgeActive : C.textMuted,
                    fontSize: TYPOGRAPHY.badge, padding: "8px 16px",
                  }}
                >
                  Present
                </Dropdown.Item>
                <Dropdown.Item
                  onClick={() => editAttendance("9")}
                  style={{
                    color: editAttValue === "9" ? C.badgeActive : C.textMuted,
                    fontSize: TYPOGRAPHY.badge, padding: "8px 16px",
                  }}
                >
                  Double Present
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>

          <hr style={{ borderColor: C.borderDefault, margin: "20px 0" }} />

          {/* Leaves Section */}
          <h4 style={{ color: C.textPrimary, fontSize: TYPOGRAPHY.h2, marginBottom: "15px" }}>
            Leaves
          </h4>

          <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", marginBottom: "20px" }}>
            <button
              onClick={() => editAttendance("6")}
              style={{
                backgroundColor: editAttValue === "6" ? C.donutPurple : 'transparent',
                border: `1px solid ${C.donutPurple}`,
                color: editAttValue === "6" ? C.textWhite : C.donutPurple,
                borderRadius: "4px", padding: "8px 16px", cursor: "pointer",
                textAlign: "center", fontSize: TYPOGRAPHY.badge, fontWeight: "600",
              }}
            >
              Paid Leave
            </button>
            <button
              onClick={() => editAttendance("8")}
              style={{
                backgroundColor: editAttValue === "8" ? C.warning : 'transparent',
                border: `1px solid ${C.warning}`,
                color: editAttValue === "8" ? C.textWhite : C.warning,
                borderRadius: "4px", padding: "8px 16px", cursor: "pointer",
                textAlign: "center", fontSize: TYPOGRAPHY.badge, fontWeight: "600",
              }}
            >
              Half Day Leave
            </button>
            <button
              onClick={() => editAttendance("7")}
              style={{
                backgroundColor: editAttValue === "7" ? C.badgeInactive : 'transparent',
                border: `1px solid ${C.badgeInactive}`,
                color: editAttValue === "7" ? C.textWhite : C.badgeInactive,
                borderRadius: "4px", padding: "8px 16px", cursor: "pointer",
                textAlign: "center", fontSize: TYPOGRAPHY.badge, fontWeight: "600",
              }}
            >
              Unpaid Leave
            </button>
          </div>

          <hr style={{ borderColor: C.borderDefault, margin: "20px 0" }} />

          {/* Punch In Display */}
          {editPunchIn && (
            <>
              <div style={{ display: "flex", alignItems: "center", gap: "8px", padding: "10px 0" }}>
                <div style={{
                  width: "28px", height: "28px", borderRadius: "50%",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  backgroundColor: getInitialColor(attendanceType.staffName || "Unknown"),
                  color: C.textWhite, fontWeight: "500", fontSize: TYPOGRAPHY.badge,
                }}>
                  {getInitials(attendanceType.staffName || "")}
                </div>
                <span style={{ color: C.textPrimary }}>{editPunchIn} In</span>
              </div>
              <hr style={{ borderColor: C.borderDefault }} />
            </>
          )}

          {/* Punch Out Display */}
          {editPunchOut && (
            <>
              <div style={{ display: "flex", alignItems: "center", gap: "8px", padding: "10px 0" }}>
                <div style={{
                  width: "28px", height: "28px", borderRadius: "50%",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  backgroundColor: getInitialColor(attendanceType.staffName || "Unknown"),
                  color: C.textWhite, fontWeight: "500", fontSize: TYPOGRAPHY.badge,
                }}>
                  {getInitials(attendanceType.staffName || "")}
                </div>
                <span style={{ color: C.textPrimary }}>{editPunchOut} Out</span>
              </div>
              <hr style={{ borderColor: C.borderDefault }} />
            </>
          )}

          {/* Add Punch Out Button */}
          {editPunchIn !== "" && editPunchOut === "" && HasPermission("Daily Attendance", "edit") && (
            <button
              style={{
                border: "none", padding: "12px 16px", borderRadius: "4px",
                backgroundColor: C.chartYellow, color: C.textWhite,
                fontSize: TYPOGRAPHY.badge, fontWeight: "600",
                cursor: "pointer", width: "100%",
                marginTop: "15px", marginBottom: "15px",
              }}
              onClick={() => setOpen(true)}
            >
              + Add Punch Out
            </button>
          )}

          {/* Add Punch In Button */}
          {editPunchIn === "" && HasPermission("Daily Attendance", "edit") && (
            <button
              style={{
                border: "none", padding: "12px 16px", borderRadius: "4px",
                backgroundColor: C.badgeActive, color: C.textWhite,
                fontSize: TYPOGRAPHY.badge, fontWeight: "600",
                cursor: "pointer", width: "100%",
                marginTop: "15px", marginBottom: "15px",
              }}
              onClick={() => setOpen(true)}
            >
              + Add Punch In
            </button>
          )}
        </div>

        {/* Drawer Footer */}
        {HasPermission("Daily Attendance", "edit") && (
          <div style={{
            padding: "20px",
            borderTop: `1px solid ${C.borderDefault}`,
            flexShrink: 0,
            backgroundColor: C.bgCard,
          }}>
            <ButtonCom
              type={"button"}
              class_name={"p_btn universal-btn"}
              text="Save"
              url_or_onclick={saveAttendance}
              width={"100%"}
            />
          </div>
        )}
      </div>
    </>
  );
};

export default DailyAttendance;